import React, { useState } from "react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Loader2, ShieldCheck, Mic2, KeyRound } from "lucide-react";

export const Profile = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", division: user?.division || "", organization: user?.organization || "", phone: user?.phone || "" });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", f);
      setUser(data);
      toast.success("Profil diperbarui");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-7" data-testid="profile-page">
      <div>
        <p className="label-xs">Akun Saya</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Profile</h1>
      </div>

      <div className="clay p-7">
        <div className="flex items-center gap-5">
          <span className="grid place-items-center h-16 w-16 rounded-2xl grad-brand text-white text-lg font-semibold">
            {(user?.name || "?").slice(0, 2).toUpperCase()}
          </span>
          <div>
            <p className="font-display text-xl font-semibold text-slate-900">{user?.name}</p>
            <p className="text-sm text-slate-500">{user?.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-medium text-brand-700">{user?.role_label}</span>
              {user?.voice_enrolled && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                  <Mic2 size={11} /> Voiceprint aktif
                </span>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={save} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="profile-form">
          {[["name", "Nama Lengkap"], ["division", "Divisi"], ["organization", "Organisasi"], ["phone", "Telepon"]].map(([k, l]) => (
            <div key={k} className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">{l}</Label>
              <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 rounded-xl" data-testid={`profile-${k}-input`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Button type="submit" className="h-11 rounded-xl grad-brand text-white px-6" disabled={busy} data-testid="save-profile-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Simpan Profil
            </Button>
          </div>
        </form>
      </div>

      <div className="clay p-6">
        <p className="label-xs">Informasi Sesi</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
          <div><p className="text-slate-500">Login terakhir</p><p className="mt-0.5 font-medium text-slate-800">{fmtDateTime(user?.last_login)}</p></div>
          <div><p className="text-slate-500">Akun dibuat</p><p className="mt-0.5 font-medium text-slate-800">{fmtDateTime(user?.created_at)}</p></div>
        </div>
        <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-slate-50 p-4">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-600" />
          <p className="text-xs leading-relaxed text-slate-500">Aktivitas akun Anda tercatat dalam audit trail sesuai kebijakan keamanan enterprise.</p>
        </div>
      </div>
    </div>
  );
};

export const ChangePassword = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== cf) { toast.error("Konfirmasi password tidak sama"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password berhasil diubah");
      setCur(""); setNw(""); setCf("");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl space-y-7" data-testid="change-password-page">
      <div>
        <p className="label-xs">Keamanan Akun</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Change Password</h1>
      </div>
      <div className="clay p-7">
        <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-50 text-brand-600"><KeyRound size={19} /></div>
        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="change-password-form">
          {[["Password saat ini", cur, setCur, "current"], ["Password baru", nw, setNw, "new"], ["Konfirmasi password baru", cf, setCf, "confirm"]].map(([l, v, set, k]) => (
            <div key={k} className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">{l}</Label>
              <Input type="password" value={v} onChange={(e) => set(e.target.value)} required minLength={6} className="h-11 rounded-xl" data-testid={`password-${k}-input`} />
            </div>
          ))}
          <Button type="submit" className="h-11 w-full rounded-xl grad-brand text-white" disabled={busy} data-testid="submit-change-password-btn">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Ubah Password
          </Button>
        </form>
      </div>
    </div>
  );
};
